import type { GeocodeV4Response } from "@be-miniapp/api"
import type { Location } from "@be-miniapp/types"

export const RIDE_LOCATION_CACHE_TTL_MS = 30 * 60 * 1000

export interface PersistedRideLocationCache {
  location: Location | null | undefined
  address: GeocodeV4Response | null | undefined
  lastLocationTimestamp?: number
  isLocationManuallyChanged?: boolean
}

export type RideLocationPermissionStatus =
  | "accepted"
  | "denied"
  | "not-asked"
  | "unavailable"

export interface ResolvedRideLocationInput extends PersistedRideLocationCache {
  locationPermissionStatus?: RideLocationPermissionStatus
}

function hasPersistedCoordinates(
  location: Location | null | undefined
): location is Location {
  return (
    Number.isFinite(location?.latitude) && Number.isFinite(location?.longitude)
  )
}

/**
 * True when the app can proceed without re-prompting for location:
 * GPS accepted with coordinates, or a fresh manual entry with geocoded address.
 */
export function hasResolvedRideLocation(
  cache: ResolvedRideLocationInput,
  ttlMs: number = RIDE_LOCATION_CACHE_TTL_MS
): boolean {
  if (!hasPersistedCoordinates(cache.location)) {
    return false
  }

  if (cache.locationPermissionStatus === "accepted") {
    return true
  }

  return (
    !!cache.isLocationManuallyChanged &&
    !!cache.address &&
    !isRideLocationCacheExpired(cache.lastLocationTimestamp, ttlMs)
  )
}

export function hasActiveTransportSession(
  activeSessions:
    | {
        active_transport_session_count?: number
      }
    | null
    | undefined
): boolean {
  return (activeSessions?.active_transport_session_count ?? 0) > 0
}

export function isRideLocationCacheExpired(
  lastLocationTimestamp?: number,
  ttlMs: number = RIDE_LOCATION_CACHE_TTL_MS
) {
  if (!lastLocationTimestamp) {
    return true
  }

  return lastLocationTimestamp <= Date.now() - ttlMs
}

export function isRideLocationCacheFresh(
  cache: PersistedRideLocationCache,
  ttlMs: number = RIDE_LOCATION_CACHE_TTL_MS
) {
  return (
    !!cache.location &&
    !!cache.address &&
    !isRideLocationCacheExpired(cache.lastLocationTimestamp, ttlMs)
  )
}

export function shouldRefreshSharedLocationInBackground(
  cache: PersistedRideLocationCache,
  ttlMs: number = RIDE_LOCATION_CACHE_TTL_MS
) {
  return (
    isRideLocationCacheFresh(cache, ttlMs) && !cache.isLocationManuallyChanged
  )
}
